/* ==========================================================================
   X-HEALTHY — facets.js
   <facet-filters>  Storefront filtering without page reloads (Section Rendering API
                    + History API), sort, load more, grid density.
   <price-range>    dual-thumb price slider synced with the number inputs.
   Regions updated after each request are marked with data-region="…".
   ========================================================================== */
(() => {
  const { theme } = window;
  const DENSITY_KEY = 'xh-grid-density';

  const readDensity = () => {
    try {
      return JSON.parse(localStorage.getItem(DENSITY_KEY)) || {};
    } catch {
      return {};
    }
  };

  /* ---------- <price-range> ---------- */
  if (!customElements.get('price-range')) {
    customElements.define(
      'price-range',
      class PriceRange extends HTMLElement {
        connectedCallback() {
          this.max = Number(this.dataset.max) || 0;
          this.thumbs = {
            min: this.querySelector('[data-thumb="min"]'),
            max: this.querySelector('[data-thumb="max"]'),
          };
          this.inputs = {
            min: this.querySelector('[data-input="min"]'),
            max: this.querySelector('[data-input="max"]'),
          };
          Object.entries(this.thumbs).forEach(([key, thumb]) => {
            thumb.addEventListener('input', () => this.onThumb(key));
            thumb.addEventListener('change', () => this.commit(key));
          });
          Object.values(this.inputs).forEach((input) => input.addEventListener('input', () => this.fromInputs()));
          this.paint();
        }

        onThumb(key) {
          let min = Number(this.thumbs.min.value);
          let max = Number(this.thumbs.max.value);
          if (min > max) {
            if (key === 'min') min = max;
            else max = min;
            this.thumbs.min.value = min;
            this.thumbs.max.value = max;
          }
          this.inputs.min.value = min > 0 ? min : '';
          this.inputs.max.value = max < this.max ? max : '';
          this.paint();
        }

        commit(key) {
          this.inputs[key].dispatchEvent(new Event('change', { bubbles: true }));
        }

        fromInputs() {
          this.thumbs.min.value = this.inputs.min.value || 0;
          this.thumbs.max.value = this.inputs.max.value || this.max;
          this.paint();
        }

        paint() {
          if (!this.max) return;
          const start = (Number(this.thumbs.min.value) / this.max) * 100;
          const end = (Number(this.thumbs.max.value) / this.max) * 100;
          this.style.setProperty('--start', `${start}%`);
          this.style.setProperty('--end', `${100 - end}%`);
        }
      }
    );
  }

  /* ---------- <facet-filters> ---------- */
  if (customElements.get('facet-filters')) return;

  customElements.define(
    'facet-filters',
    class FacetFilters extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.cache = new Map();
        this.debouncedApply = theme.utils.debounce((form) => this.apply(form), 650);

        this.addEventListener('change', (event) => this.onChange(event));
        this.addEventListener('input', (event) => this.onInput(event));
        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('submit', (event) => {
          if (event.target.matches('[data-facets-form]')) {
            event.preventDefault();
            this.apply(event.target);
          }
        });

        this.onPopState = () => this.render(window.location.href, false);
        window.addEventListener('popstate', this.onPopState);
        this.applyDensity();
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
      }

      /* ----- Events ----- */
      onChange(event) {
        const target = event.target;
        if (target.matches('[data-sort-select]')) {
          const params = new URLSearchParams(window.location.search);
          params.set('sort_by', target.value);
          params.delete('page');
          this.render(`${window.location.pathname}?${params}`);
          return;
        }
        const form = target.closest('[data-facets-form]');
        if (!form || target.matches('[data-facet-search], [data-thumb]')) return;
        if (target.matches('[data-input]')) this.debouncedApply(form);
        else this.apply(form);
      }

      onInput(event) {
        const target = event.target;
        if (target.matches('[data-facet-search]')) {
          const term = target.value.trim().toLowerCase();
          const list = target.closest('.facet__body').querySelector('.facet__list');
          list.querySelectorAll('.facet__value').forEach((item) => {
            const match = !term || item.dataset.label.includes(term);
            item.hidden = term ? !match : item.hasAttribute('data-extra') && !list.hasAttribute('data-expanded');
          });
        }
      }

      onClick(event) {
        const link = event.target.closest('[data-facet-link]');
        if (link) {
          event.preventDefault();
          this.render(link.href);
          return;
        }
        const more = event.target.closest('[data-facet-more]');
        if (more) {
          const list = more.closest('.facet__body').querySelector('.facet__list');
          const expanded = list.toggleAttribute('data-expanded');
          list.querySelectorAll('[data-extra]').forEach((item) => (item.hidden = !expanded));
          more.querySelector('[data-more]').hidden = expanded;
          more.querySelector('[data-less]').hidden = !expanded;
          return;
        }
        const loadMore = event.target.closest('[data-load-more]');
        if (loadMore) {
          event.preventDefault();
          this.loadMore(loadMore);
          return;
        }
        const density = event.target.closest('[data-density]');
        if (density) this.setDensity(Number(density.dataset.density));
      }

      /* ----- Filtering ----- */
      apply(form) {
        const params = new URLSearchParams();
        new FormData(form).forEach((value, key) => {
          if (value !== '') params.append(key, value);
        });
        this.render(`${window.location.pathname}?${params}`);
      }

      async render(url, push = true) {
        const target = new URL(url, window.location.origin);
        target.searchParams.delete('section_id');
        target.searchParams.delete('page');
        const key = target.toString();

        this.controller?.abort();
        this.controller = new AbortController();
        this.setAttribute('loading', '');
        this.querySelector('[data-region="grid"]')?.setAttribute('aria-busy', 'true');

        try {
          let html = this.cache.get(key);
          if (!html) {
            const fetchUrl = new URL(key);
            fetchUrl.searchParams.set('section_id', this.sectionId);
            const response = await fetch(fetchUrl, { signal: this.controller.signal });
            if (!response.ok) throw new Error(response.status);
            html = await response.text();
            this.cache.set(key, html);
          }
          const doc = new DOMParser().parseFromString(html, 'text/html');
          this.swapRegions(doc);
          if (push) window.history.pushState({ facets: true }, '', key);
          const count = this.querySelector('[data-region="count"]')?.textContent.trim();
          if (count) theme.utils.announce(count);
        } catch (error) {
          if (error.name !== 'AbortError') theme.toast({ message: theme.strings.error, type: 'error' });
        } finally {
          this.removeAttribute('loading');
          this.querySelector('[data-region="grid"]')?.removeAttribute('aria-busy');
        }
      }

      swapRegions(doc) {
        const openDetails = new Set([...this.querySelectorAll('details[data-facet][id]')].filter((d) => d.open).map((d) => d.id));
        const closedDetails = new Set([...this.querySelectorAll('details[data-facet][id]')].filter((d) => !d.open).map((d) => d.id));
        const focusedId = document.activeElement?.id;
        const scrollers = [...this.querySelectorAll('[data-region="sidebar"], [data-region="drawer"]')].map((el) => el.scrollTop);

        this.querySelectorAll('[data-region]').forEach((region) => {
          const name = region.dataset.region;
          const index = [...this.querySelectorAll(`[data-region="${name}"]`)].indexOf(region);
          const fresh = doc.querySelectorAll(`[data-region="${name}"]`)[index];
          if (fresh) region.innerHTML = fresh.innerHTML;
        });

        // Keep the accordion state the shopper chose
        this.querySelectorAll('details[data-facet][id]').forEach((details) => {
          if (openDetails.has(details.id)) details.open = true;
          if (closedDetails.has(details.id)) details.open = false;
        });
        this.querySelectorAll('[data-region="sidebar"], [data-region="drawer"]').forEach((el, i) => (el.scrollTop = scrollers[i] || 0));
        if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

        // Sort select mirrors the URL
        const sort = new URL(window.location.href).searchParams.get('sort_by');
        const fresh = doc.querySelector('[data-sort-select]');
        const select = this.querySelector('[data-sort-select]');
        if (select && fresh) select.value = fresh.value || sort || select.value;

        this.applyDensity();
        theme.initReveal?.(this);
      }

      /* ----- Load more ----- */
      async loadMore(button) {
        if (button.getAttribute('aria-busy') === 'true') return;
        button.setAttribute('aria-busy', 'true');
        try {
          const url = new URL(button.href, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);
          const response = await fetch(url);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const grid = this.querySelector('[data-product-grid]');
          const items = doc.querySelectorAll('[data-product-grid] > li');
          const firstNew = items[0];
          items.forEach((item) => grid.append(item));
          const pagination = this.querySelector('[data-region="pagination"]');
          const freshPagination = doc.querySelector('[data-region="pagination"]');
          if (pagination && freshPagination) pagination.innerHTML = freshPagination.innerHTML;
          // Move focus to the first newly loaded product for keyboard users
          firstNew?.querySelector('a')?.focus({ preventScroll: true });
        } catch (error) {
          window.location.href = button.href;
        }
      }

      /* ----- Grid density ----- */
      setDensity(cols) {
        const pref = readDensity();
        if (cols <= 2) pref.mobile = cols;
        else pref.desktop = cols;
        try {
          localStorage.setItem(DENSITY_KEY, JSON.stringify(pref));
        } catch {
          /* ignore */
        }
        this.applyDensity();
      }

      applyDensity() {
        const pref = readDensity();
        const grid = this.querySelector('[data-product-grid]');
        if (grid) {
          if (pref.mobile) grid.dataset.colsMobile = pref.mobile;
          if (pref.desktop) grid.dataset.colsDesktop = pref.desktop;
        }
        const current = { mobile: grid?.dataset.colsMobile, desktop: grid?.dataset.colsDesktop };
        this.querySelectorAll('[data-density]').forEach((button) => {
          const cols = button.dataset.density;
          const active = Number(cols) <= 2 ? current.mobile === cols : current.desktop === cols;
          button.setAttribute('aria-pressed', String(active));
        });
      }
    }
  );
})();
